import { useQuery } from '@tanstack/react-query';
import type { JsonValue } from 'type-fest';

import { useRequest, queryClient, type QueryFunction } from '@frontend/api';

import type { ActionListResponse, ActionFilterQueryParams } from './types';

export type GetActionsQueryParams = Omit<ActionFilterQueryParams, 'quoteId'>;

export const isUseGetActionsQueryEnabled = (query: ActionFilterQueryParams) => {
  // We cannot have a quoteId in our actions list query
  if (query.quoteId) return false;
  // We need at least one of componentId, componentIds, or quoteItemId
  if (!query.componentId && !query.componentIds && !query.quoteItemId)
    return false;

  return Object.keys(query).length > 0;
};

const partialUseGetActionsQueryKey = () => ['actions', 'list'];

const getUseGetActionsKey = (params: GetActionsQueryParams) =>
  partialUseGetActionsQueryKey().concat([JSON.stringify(params)]);

export const useGetActions: QueryFunction<
  ActionListResponse,
  [GetActionsQueryParams],
  ReturnType<typeof getUseGetActionsKey>
> = (params, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetActionsKey(params),
    queryFn: ({ signal }) => {
      return request<ActionListResponse & JsonValue>(`/rules/actions`, {
        query: params,
        init: {
          signal,
        },
      });
    },
  });
};

export const invalidateActionsQuery = (params: GetActionsQueryParams) => {
  queryClient.invalidateQueries({ queryKey: getUseGetActionsKey(params) });
};

export const invalidateAllActionsQueries = () => {
  queryClient.invalidateQueries({ queryKey: partialUseGetActionsQueryKey() });
};
